import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Button, Icon, Popover } from '@kontentino/ui';
import { faFolder } from '@fortawesome/pro-regular-svg-icons/faFolder';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import SearchTextInput from 'app/components/SearchTextInput';
import { AlbumTreeNode } from 'app/modules/mediaLibraryPage/hooks/useAlbumTreeForMove';

interface Props {
  albumTree: AlbumTreeNode[];
  activeAlbumId: string | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry(): void;
  onSelect(albumId: string): void;
}

const MediaLibraryPickerAlbumSelect: FC<Props> = ({
  albumTree,
  activeAlbumId,
  isLoading,
  isError,
  onRetry,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const activeAlbum = useMemo(
    () => albumTree.find((node) => node.album.id === activeAlbumId)?.album,
    [albumTree, activeAlbumId],
  );

  const filteredAlbums = useMemo(() => {
    const search = searchValue.toLowerCase().trim();
    if (!search) return albumTree;
    return albumTree.filter((node) =>
      node.album.name.toLowerCase().includes(search),
    );
  }, [albumTree, searchValue]);

  const handleSelect = (albumId: string) => {
    onSelect(albumId);
    setSearchValue('');
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div
        className="tw-flex tw-h-[36px] tw-w-full tw-cursor-not-allowed tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-grayscale-10 tw-px-2 tw-opacity-50"
        data-cy="media-library-picker_album-select"
      >
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <Icon icon={faFolder} className="tw-shrink-0 tw-text-grayscale-100" />
          <span className="tw-truncate tw-text-md tw-text-grayscale-180">
            {t('loading')}
          </span>
        </div>
        <Icon icon={faChevronDown} className="tw-shrink-0" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-2 tw-py-1"
        data-cy="media-library-picker_album-select-error"
      >
        <span className="tw-text-red-100 tw-text-sm">
          {t('somethingWentWrong')}
        </span>
        <Button
          variant="secondary"
          size="small"
          onClick={onRetry}
          data-cy="media-library-picker_album-select-retry"
        >
          {t('retry')}
        </Button>
      </div>
    );
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild data-cy="media-library-picker_album-select">
        <div className="tw-flex tw-h-[36px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2 tw-rounded tw-bg-grayscale-10 tw-px-2">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
            <Icon
              icon={faFolder}
              className="tw-shrink-0 tw-text-grayscale-100"
            />
            <span className="tw-truncate tw-text-md tw-text-grayscale-180">
              {activeAlbum?.name ?? t('mediaLibrary.picker.selectAlbum')}
            </span>
          </div>
          <Icon icon={faChevronDown} className="tw-shrink-0" />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="tw-z-[1001] tw-w-[var(--radix-popover-trigger-width)] tw-overflow-hidden tw-rounded-lg tw-bg-white tw-shadow-2xl"
        >
          <div className="tw-p-1">
            <SearchTextInput
              type="text"
              placeholder={t('search')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onClear={() => setSearchValue('')}
              className="tw-w-full !tw-border-none"
              data-cy="media-library-picker_album-search"
            />
          </div>
          <div className="tw-flex tw-max-h-[350px] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-p-2">
            {filteredAlbums.map(({ album, depth }) => {
              const isActive = album.id === activeAlbumId;
              return (
                <button
                  key={album.id}
                  type="button"
                  onClick={() => handleSelect(album.id)}
                  data-cy="media-library-picker_album-row"
                  className={clsx(
                    'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-py-2 tw-pr-2 tw-text-left tw-text-sm tw-transition-colors',
                    {
                      'tw-bg-primary-10 tw-text-primary-100': isActive,
                      'tw-text-grayscale-180 hover:tw-bg-grayscale-10':
                        !isActive,
                    },
                  )}
                  style={{ paddingLeft: `${8 + depth * 16}px` }}
                >
                  <Icon
                    icon={faFolder}
                    className={clsx('tw-shrink-0', {
                      'tw-text-primary-100': isActive,
                      'tw-text-grayscale-100': !isActive,
                    })}
                  />
                  <span className="tw-flex-1 tw-truncate">{album.name}</span>
                </button>
              );
            })}
            {filteredAlbums.length === 0 && (
              <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-grayscale-100">
                {t('mediaLibrary.picker.noAlbumsFound')}
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default MediaLibraryPickerAlbumSelect;
